import { ReactNode, useEffect } from 'react';
import posthog, { BeforeSendFn, CaptureResult } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { ENV } from 'shared/ENV';
import { Capacitor } from '@capacitor/core';
import { getAppVersion } from 'shared/envUtils';
import { Device, DeviceInfo } from '@capacitor/device';
import { semverToInteger } from './PostHogProviderWrapper.utils';
import { useAuthState } from '../../../state/useAuthState';
import { usePostHogIdentification } from './usePostHogIdentification';

type PostHogSessionSuperProperties = {
  appVersion: string;
  appVersionInteger: number;
  capacitorIOSVersion?: number;
  capacitorAndroidSDKVersion?: number;
};

type PostHogSuperProperties = {
  isNativePlatform: boolean;
  capacitorPlatform: string;
};

type GetOSVersionDetails = {
  capacitorIOSVersion?: number;
  capacitorAndroidSDKVersion?: number;
};

const getOsBeforeSendOverride = (deviceInfo: DeviceInfo): BeforeSendFn => {
  const overrideNativeOsVersion = (
    captureResult: CaptureResult | null,
  ): CaptureResult | null => {
    if (!captureResult) {
      return null;
    }

    if (!Capacitor.isNativePlatform() || !captureResult.properties) {
      return captureResult;
    }

    // eslint-disable-next-line no-param-reassign
    captureResult.properties.$os_version = deviceInfo.osVersion;

    return captureResult;
  };

  return overrideNativeOsVersion;
};

const initPostHog = async (): Promise<void> => {
  const deviceInfo = await Device.getInfo();

  posthog.init(ENV.VITE_POSTHOG_PUBLIC_KEY, {
    api_host: 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
    name: import.meta.env.VITE_POSTHOG_ENV,
    capture_pageview: false, // doing this manually: https://posthog.com/tutorials/single-page-app-pageviews
    capture_pageleave: true, // needed for PostHog to determine bounce rate
    persistence: 'localStorage', // 3rd party cookies may not always work on native apps
    before_send: [getOsBeforeSendOverride(deviceInfo)],
  });
};

const getOSVersionDetails = async (): Promise<GetOSVersionDetails> => {
  const platform = Capacitor.getPlatform();
  const deviceInfo = await Device.getInfo();

  if (platform === 'ios') {
    return {
      capacitorIOSVersion: deviceInfo.iOSVersion,
    };
  }

  if (platform === 'android') {
    return {
      capacitorAndroidSDKVersion: deviceInfo.androidSDKVersion,
    };
  }

  return {};
};

/**
 * Set "super properties" for that will be sent with every event to PostHog.
 */
const setSuperProperties = async (): Promise<void> => {
  const appVersion = await getAppVersion();

  const osVersionDetails = await getOSVersionDetails();

  const sessionProperties: PostHogSessionSuperProperties = {
    appVersion,
    appVersionInteger: semverToInteger(appVersion),
    ...osVersionDetails,
  };

  const superProperties: PostHogSuperProperties = {
    isNativePlatform: Capacitor.isNativePlatform(),
    capacitorPlatform: Capacitor.getPlatform(),
  };

  // register() should come after register_for_session() because register_for_session()
  // will unset everything from register() (according to docs)
  posthog.register_for_session(sessionProperties);
  posthog.register(superProperties);
};

export const PostHogProviderWrapper = ({
  children,
}: {
  children: ReactNode;
}): ReactNode => {
  if (!ENV.VITE_POSTHOG_ENV || !ENV.VITE_POSTHOG_PUBLIC_KEY) {
    return children;
  }

  const {
    user,
    permissions: { isInternalUser },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useAuthState();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    initPostHog();
    setSuperProperties();
  }, []);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  usePostHogIdentification({
    user,
    isInternalUser,
  });

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
};
